import { useEffect, useMemo, useState } from 'react';
import { Sidebar, type ConsoleSection } from '../components/layout/Sidebar';
import { useAuth } from '../context/AuthContext';
import {
  canAccessKyb,
  canAccessMerchants,
  canAccessAuditLogs,
  canAccessCargo,
  canAccessStaff,
  canAccessSystem,
  canAccessTransactions,
  canEditCargoCommission,
} from '../utils/roles';
import { AuditSection } from './sections/AuditSection';
import { CargoCommissionSection } from './sections/CargoCommissionSection';
import { CargoSection } from './sections/CargoSection';
import { DashboardSection } from './sections/DashboardSection';
import { KybSection } from './sections/KybSection';
import { MerchantsSection } from './sections/MerchantsSection';
import { StaffSection } from './sections/StaffSection';
import { SystemSection } from './sections/SystemSection';
import { TransactionsSection } from './sections/TransactionsSection';

function isAllowedSection(section: ConsoleSection, role: ReturnType<typeof useAuth>['role']): boolean {
  if (section === 'merchants') return canAccessMerchants(role);
  if (section === 'kyb') return canAccessKyb(role);
  if (section === 'transactions') return canAccessTransactions(role);
  if (section === 'audit') return canAccessAuditLogs(role);
  if (section === 'staff') return canAccessStaff(role);
  if (section === 'cargo') return canAccessCargo(role);
  if (section === 'cargoCommission') return canEditCargoCommission(role);
  if (section === 'system') return canAccessSystem(role);
  return true;
}

export function ConsolePage() {
  const { role } = useAuth();
  const [active, setActive] = useState<ConsoleSection>('dashboard');

  useEffect(() => {
    if (!isAllowedSection(active, role)) {
      setActive('dashboard');
    }
  }, [active, role]);

  const content = useMemo(() => {
    switch (active) {
      case 'merchants':
        return <MerchantsSection />;
      case 'cargo':
        return <CargoSection />;
      case 'cargoCommission':
        return <CargoCommissionSection />;
      case 'system':
        return <SystemSection />;
      case 'kyb':
        return <KybSection />;
      case 'transactions':
        return <TransactionsSection />;
      case 'audit':
        return <AuditSection />;
      case 'staff':
        return <StaffSection />;
      case 'dashboard':
      default:
        return <DashboardSection />;
    }
  }, [active]);

  return (
    <div className="dash-layout console-layout">
      <Sidebar active={active} onSelect={setActive} />
      <div className="dash-main console-main">{content}</div>
    </div>
  );
}
